<template>
  <div class="m-funnel">
    <div v-for="(b, i) in bands" :key="i" class="m-funnel__row">
      <!-- The tapered fill lives on its own layer so `clip-path` only ever
           clips the colour, never the label text (F-TT-03: narrow tip bands
           used to clip-path the label along with the band, truncating e.g.
           "Attributed" to "…ribu…"). -->
      <div class="m-funnel__band" :style="{ left: b.left, width: b.width, background: b.fill }"></div>
      <span v-if="b.drop !== null" class="m-funnel__drop" :class="{ 'm-funnel__drop--up': b.drop > 0 }">{{ b.drop > 0 ? '+' : '' }}{{ b.drop }}%</span>
      <span class="m-funnel__lab">{{ b.label }}</span>
      <span class="m-funnel__cnt">{{ b.count }}<small>{{ b.pct }}%</small></span>
    </div>
  </div>
</template>

<script>
/**
 * MusubiPyramidFunnel — a centred-bar funnel. Each stage is a centred bar
 * whose width is EXACTLY its own share of the largest stage; drop-off vs the
 * previous stage is shown on the side.
 *
 *   <MusubiPyramidFunnel :stages="[
 *     { label:'Details entered', count:412 },
 *     { label:'Booth started',   count:388 },
 *     …
 *   ]" />
 *
 * Why bars, not trapezoids (Toby, 2026-07-15): the old geometry interpolated
 * each band's bottom edge toward the NEXT stage's width, so any stage larger
 * than its predecessor made the band flare outward — "not funnel shaped" —
 * and no band's silhouette corresponded to the number printed beside it.
 * Real pipeline stages aren't guaranteed monotonic, and the design canon is
 * "funnels absolute": every width must match its own count. Centred bars do
 * exactly that — they read as a funnel when the data funnels, and never lie
 * when it doesn't. Zero/tiny counts keep a visible hairline (display floor)
 * while the printed count stays truthful.
 *
 * Pure presentational; counts drive the geometry. Colours interpolate the
 * theme accent so it reads as one funnel.
 */
export default {
  name: 'MusubiPyramidFunnel',
  props: {
    /** [{ label, count }] in order, widest first. */
    stages: { type: Array, required: true },
  },
  computed: {
    bands() {
      const counts = this.stages.map((s) => Number(s.count) || 0);
      const max = Math.max(...counts, 1);
      const w = counts.map((c) => c / max); // fraction of the largest stage
      return this.stages.map((s, i) => {
        // Display floor: a non-zero count is always a visible bar (4%), a
        // zero count keeps a 1.5% hairline so the row reads as "empty stage",
        // not a rendering glitch. The printed count is always the truth.
        const shown = counts[i] > 0 ? Math.max(w[i], 0.04) : 0.015;
        const width = (shown * 100).toFixed(1) + '%';
        const left = (((1 - shown) / 2) * 100).toFixed(1) + '%';
        // fade the accent down the funnel so bands read distinctly
        const alpha = Math.max(0.45, 1 - i * 0.13).toFixed(2);
        const fill = `linear-gradient(90deg, color-mix(in srgb, var(--m-accent) ${Math.round(alpha * 100)}%, #fff) , color-mix(in srgb, var(--m-accent) ${Math.round(alpha * 70)}%, #fff))`;
        const drop = i > 0 && counts[i - 1] > 0 ? Math.round((counts[i] / counts[i - 1] - 1) * 100) : null;
        return { label: s.label, count: s.count, pct: Math.round(w[i] * 100), left, width, fill, drop };
      });
    },
  },
};
</script>

<style scoped>
.m-funnel { width: 100%; max-width: 460px; margin: 4px auto; padding: 0 86px; position: relative; }
.m-funnel__row { height: 52px; margin-bottom: 3px; position: relative; }
.m-funnel__band { position: absolute; top: 0; bottom: 0; border-radius: 4px; }
/* Label sits on its own layer, full row width, so it is never subject to the
   band's clip-path — narrow tip segments (e.g. "Attributed") no longer get
   their text clipped along with the trapezoid. */
.m-funnel__lab {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 0.82rem; color: #fff;
  /* A wider dark halo (not just a drop-shadow) so the label — now unclipped
     and spanning the full row — stays legible even where a narrow tip band
     leaves it partly over the (light) card background rather than the fill. */
  text-shadow: 0 0 4px rgba(15, 23, 42, 0.55), 0 0 4px rgba(15, 23, 42, 0.55), 0 1px 2px rgba(15, 23, 42, 0.35);
  white-space: nowrap; padding: 0 4px; pointer-events: none;
}
.m-funnel__cnt {
  position: absolute; right: -82px; top: 50%; transform: translateY(-50%);
  width: 72px; text-align: left; color: var(--m-text-primary); font-weight: 750; font-size: 0.85rem;
}
.m-funnel__cnt small { display: block; color: var(--m-text-tertiary); font-weight: 600; font-size: 0.68rem; }
.m-funnel__drop {
  position: absolute; left: -74px; top: 50%; transform: translateY(-50%);
  width: 62px; text-align: right; color: var(--m-status-danger); font-size: 0.72rem; font-weight: 700;
}
/* A stage GAINING on its predecessor isn't a loss — neutral, not danger-red. */
.m-funnel__drop--up { color: var(--m-text-tertiary); }
</style>
